// MachinePoolCard - the org dashboard's view of the analysis fleet: how many
// machines are in use, how many stand available, how many of those are warm,
// and what they run.
//
// Fed from the member-visible intake catalog (GET /api/systems), the same
// answer the submit page reads - aggregate counts per base image, never
// machine rows. The admin machines page is the operator's view; this card is
// the analyst's "will my submission wait" at a glance.

import { hasOSGlyph, OSIcon } from '@/components/OSIcon';
import { StatCell, StatStrip } from '@/components/ui';
import { osFamilyLabel, type SystemImageChoice } from '@/lib/api';

/** The VM-lane pool summed across every image. Bare metal stays out: the
 *  dashboard does not dispatch on it, and the catalog carries no ready/busy
 *  split for that lane. */
export function poolTotals(images: readonly SystemImageChoice[]) {
  let inUse = 0;
  let available = 0;
  let warm = 0;
  let total = 0;
  for (const img of images) {
    inUse += img.busy_machines;
    available += img.ready_machines;
    warm += img.warm_machines;
    total += img.machines;
  }
  return { inUse, available, warm, total };
}

export function MachinePoolCard({
  images,
  loading,
}: {
  images: readonly SystemImageChoice[] | undefined;
  loading: boolean;
}) {
  const totals = poolTotals(images ?? []);

  // Grouped by guest OS rather than by image name: "3 × Windows 11" is what an
  // analyst weighs a submission against; which image build serves it is an
  // operator's question. Warmth rides along per OS - the strip's fleet-wide
  // warm count cannot say WHICH guest a submission would start on without
  // waiting, and that is the question this card exists to answer. Zero is
  // stated rather than omitted: a cold pool is a fact, not a gap.
  const byOS = new Map<string, { machines: number; warm: number }>();
  for (const img of images ?? []) {
    if (img.machines > 0) {
      const at = byOS.get(img.os_family) ?? { machines: 0, warm: 0 };
      at.machines += img.machines;
      at.warm += img.warm_machines;
      byOS.set(img.os_family, at);
    }
  }

  return (
    <div className="card kpi-card">
      <div className="kpi-header">
        <span className="kpi-title">Machine pool</span>
        {!loading &&
          (totals.inUse > 0 ? (
            <span className="status-indicator status-warning">{totals.inUse} in use</span>
          ) : (
            <span className="status-indicator status-good">Idle</span>
          ))}
      </div>
      {loading ? (
        <div className="h-[72px] animate-pulse rounded-lg bg-ink-850/60" />
      ) : (
        <StatStrip
          footer={
            byOS.size > 0 ? (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-mini text-ink-400">
                {[...byOS.entries()].map(([family, n]) => (
                  <span key={family} className="inline-flex items-center gap-1.5">
                    {hasOSGlyph(family) && (
                      <span aria-hidden="true">
                        <OSIcon family={family} compact />
                      </span>
                    )}
                    {osFamilyLabel(family)}
                    <span className="tabular-nums">· {n.machines}</span>
                    <span className="tabular-nums">· {n.warm} warm</span>
                  </span>
                ))}
              </div>
            ) : undefined
          }
        >
          <StatCell label="in use" value={totals.inUse} tint={totals.inUse > 0 ? 'accent' : undefined} />
          <StatCell
            label="available"
            value={totals.available}
            tint={totals.available > 0 ? 'clean' : undefined}
          />
          <StatCell label="warm" value={totals.warm} tint={totals.warm > 0 ? 'accent' : undefined} />
        </StatStrip>
      )}
    </div>
  );
}
